import { useEffect } from "react";
import { footer } from "../content/site";
import { Container } from "./ui";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Google Play account-deletion URL. Static on purpose (no Firestore read), so
// it always renders. Every statement mirrors the published Privacy Policy
// §12 (Retention) and §13 (Account deletion) — keep the two in sync.
const { email, phone, phoneHref } = footer.contact;
const SUBJECT = "Delete my PARKORA account";
const BODY = [
  "Please delete my PARKORA account and associated personal data.",
  "",
  "Registered mobile number:",
  "Name on the account:",
  "Account type (customer or owner):",
].join("\n");
const mailto = `mailto:${email}?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(BODY)}`;

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-lg font-extrabold text-navy">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-navy/70">{children}</div>
    </section>
  );
}

export default function DeleteAccountPage() {
  useEffect(() => {
    document.title = "Delete your account | PARKORA";
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Navbar />
      <main className="min-h-[60vh] pt-28 pb-20">
        <Container className="max-w-3xl">
          <h1 className="text-3xl font-extrabold text-navy sm:text-4xl">
            PARKORA — Account and Data Deletion Request
          </h1>
          <p className="mt-4 leading-relaxed text-navy/70">
            You can ask us to delete your PARKORA account and the personal information linked to it. This page
            applies to the PARKORA app published by Maya Media Works. You do not need to sign in or have the app
            installed to make a request.
          </p>

          <Section title="How to request deletion">
            <p>
              <strong className="font-bold text-navy">Option 1 — by email (works even if you have uninstalled the app).</strong>{" "}
              Email <a href={`mailto:${email}`} className="font-semibold text-primary underline">{email}</a> with
              the subject <strong className="font-bold text-navy">"{SUBJECT}"</strong>, sending it from your
              registered details and including:
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>the mobile number registered with PARKORA,</li>
              <li>the name on the account, and</li>
              <li>whether it is a customer or owner account.</li>
            </ul>
            <a
              href={mailto}
              className="focus-ring inline-block rounded-xl bg-primary px-5 py-3 text-sm font-extrabold text-white"
            >
              Email a deletion request
            </a>
            <p>
              <strong className="font-bold text-navy">Option 2 — in the app.</strong> If you can still sign in, open
              Profile and tap <strong className="font-bold text-navy">Request account deletion</strong>.
            </p>
          </Section>

          <Section title="How we verify your request">
            <p>
              To protect you from someone else deleting your account, identity verification may be required: we
              confirm that the request really comes from the account holder before anything is deleted. We may
              contact you on the mobile number registered with PARKORA to do this. <strong className="font-bold text-navy">We will never ask for
              your OTP</strong> — do not share it with anyone.
            </p>
          </Section>

          <Section title="What happens next">
            <p>
              Deletion is not automatic or instant. Every request, whether sent by email or from the app, is
              reviewed and processed manually by the PARKORA team. After confirming the request, we settle anything
              still open on the account, such as an active booking, a pending refund or an owner payout, and then
              delete your account.
            </p>
            <p>
              When an account is deleted, we delete or anonymise the personal information linked to it — for
              example your mobile number and name, saved vehicles, driving licence image and, for owners,
              verification (KYC) documents and payout details. Deletion cannot be undone.
            </p>
          </Section>

          <Section title="What we may keep">
            <p>
              We keep records we must retain for legal, tax, accounting, fraud-prevention or dispute purposes (such
              as payment and booking records), and only for as long as needed for those purposes.
            </p>
          </Section>

          <Section title="Website sign-ups">
            <p>
              If you joined the website waitlist or registered interest as a parking owner, you can also ask us to
              remove those details by emailing <a href={`mailto:${email}`} className="font-semibold text-primary underline">{email}</a>.
            </p>
          </Section>

          <Section title="Need help?">
            <p>
              Email <a href={`mailto:${email}`} className="font-semibold text-primary underline">{email}</a> or call{" "}
              <a href={phoneHref} className="font-semibold text-primary underline">{phone}</a>. For full details, see
              our <a href="/privacy-policy" className="font-semibold text-primary underline">Privacy Policy</a>.
            </p>
          </Section>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
